/**
 * Content and exercise plans behind the v2 mini-lesson.
 *
 * Two different languages are in play on every screen. The *prompt* is shown in
 * the learner's interface language and comes from the `lesson.words.*` and
 * `lesson.phrases.*` translation keys. The *answer* is in the language they
 * chose to learn and comes from the tables here.
 *
 * Only the courses LingoChat sells most of carry a table; anything else falls
 * back to English, which keeps the lesson playable without shipping a
 * half-checked dictionary for fifteen languages.
 */

export type LessonWordId = 'cat' | 'dog' | 'water' | 'milk' | 'mom'

export type LessonPhraseId =
  | 'goodMorning'
  | 'thankYou'
  | 'howAreYou'
  | 'goodbye'

/** The answers to the `level-question` step, stored under `comfortLevel`. */
export type LessonLevel =
  | 'beginner'
  | 'elementary'
  | 'intermediate'
  | 'advanced'

export const LESSON_WORD_IDS: LessonWordId[] = [
  'cat',
  'dog',
  'water',
  'milk',
  'mom',
]

export const LESSON_PHRASE_IDS: LessonPhraseId[] = [
  'goodMorning',
  'thankYou',
  'howAreYou',
  'goodbye',
]

/** Stands in for Duolingo's illustrations on the picture-choice cards. */
export const LESSON_WORD_EMOJI: Record<LessonWordId, string> = {
  cat: '🐱',
  dog: '🐶',
  milk: '🥛',
  mom: '👩‍🍼',
  water: '💧',
}

interface LanguageContent {
  phrases: Record<LessonPhraseId, string>
  /**
   * "I drink water" split into tiles in the target language's own word order,
   * plus plausible decoys. Tokenised by hand rather than by splitting on
   * spaces, because Japanese and Chinese do not put spaces between words.
   */
  sentence: { decoys: string[]; tokens: string[] }
  words: Record<LessonWordId, string>
}

const ENGLISH: LanguageContent = {
  phrases: {
    goodbye: 'goodbye',
    goodMorning: 'good morning',
    howAreYou: 'how are you?',
    thankYou: 'thank you',
  },
  sentence: { decoys: ['milk', 'eat'], tokens: ['I', 'drink', 'water'] },
  words: { cat: 'cat', dog: 'dog', milk: 'milk', mom: 'mom', water: 'water' },
}

const CONTENT_BY_LANGUAGE: Record<string, LanguageContent> = {
  de: {
    phrases: {
      goodbye: 'auf Wiedersehen',
      goodMorning: 'guten Morgen',
      howAreYou: 'wie geht es dir?',
      thankYou: 'danke',
    },
    sentence: {
      decoys: ['Milch', 'esse'],
      tokens: ['Ich', 'trinke', 'Wasser'],
    },
    words: {
      cat: 'Katze',
      dog: 'Hund',
      milk: 'Milch',
      mom: 'Mama',
      water: 'Wasser',
    },
  },
  en: ENGLISH,
  es: {
    phrases: {
      goodbye: 'adiós',
      goodMorning: 'buenos días',
      howAreYou: '¿cómo estás?',
      thankYou: 'gracias',
    },
    sentence: { decoys: ['leche', 'como'], tokens: ['Yo', 'bebo', 'agua'] },
    words: {
      cat: 'gato',
      dog: 'perro',
      milk: 'leche',
      mom: 'mamá',
      water: 'agua',
    },
  },
  fr: {
    phrases: {
      goodbye: 'au revoir',
      goodMorning: 'bonjour',
      howAreYou: 'comment ça va ?',
      thankYou: 'merci',
    },
    sentence: {
      decoys: ['lait', 'mange'],
      tokens: ['Je', 'bois', 'de', "l'eau"],
    },
    words: {
      cat: 'chat',
      dog: 'chien',
      milk: 'lait',
      mom: 'maman',
      water: 'eau',
    },
  },
  it: {
    phrases: {
      goodbye: 'arrivederci',
      goodMorning: 'buongiorno',
      howAreYou: 'come stai?',
      thankYou: 'grazie',
    },
    sentence: { decoys: ['latte', 'mangio'], tokens: ['Io', 'bevo', 'acqua'] },
    words: {
      cat: 'gatto',
      dog: 'cane',
      milk: 'latte',
      mom: 'mamma',
      water: 'acqua',
    },
  },
  ja: {
    phrases: {
      goodbye: 'さようなら',
      goodMorning: 'おはよう',
      howAreYou: 'お元気ですか',
      thankYou: 'ありがとう',
    },
    sentence: {
      decoys: ['ぎゅうにゅうを', 'たべます'],
      tokens: ['わたしは', 'みずを', 'のみます'],
    },
    words: {
      cat: 'ねこ',
      dog: 'いぬ',
      milk: 'ぎゅうにゅう',
      mom: 'おかあさん',
      water: 'みず',
    },
  },
  ko: {
    phrases: {
      goodbye: '안녕히 가세요',
      goodMorning: '좋은 아침이에요',
      howAreYou: '어떻게 지내세요?',
      thankYou: '감사합니다',
    },
    sentence: {
      decoys: ['우유를', '먹어요'],
      tokens: ['저는', '물을', '마셔요'],
    },
    words: {
      cat: '고양이',
      dog: '강아지',
      milk: '우유',
      mom: '엄마',
      water: '물',
    },
  },
  pt: {
    phrases: {
      goodbye: 'adeus',
      goodMorning: 'bom dia',
      howAreYou: 'como está?',
      thankYou: 'obrigado',
    },
    sentence: { decoys: ['leite', 'como'], tokens: ['Eu', 'bebo', 'água'] },
    words: {
      cat: 'gato',
      dog: 'cachorro',
      milk: 'leite',
      mom: 'mãe',
      water: 'água',
    },
  },
  zh: {
    phrases: {
      goodbye: '再见',
      goodMorning: '早上好',
      howAreYou: '你好吗？',
      thankYou: '谢谢',
    },
    sentence: { decoys: ['牛奶', '吃'], tokens: ['我', '喝', '水'] },
    words: { cat: '猫', dog: '狗', milk: '牛奶', mom: '妈妈', water: '水' },
  },
}

export function getLessonContent(languageCode: string): LanguageContent {
  return CONTENT_BY_LANGUAGE[languageCode] ?? ENGLISH
}

export type LessonExercise =
  | {
      options: LessonPhraseId[]
      prompt: LessonPhraseId
      type: 'phrase-meaning'
    }
  | { options: LessonWordId[]; prompt: LessonWordId; type: 'image-choice' }
  | { options: LessonWordId[]; prompt: LessonWordId; type: 'meaning' }
  | { phrases: LessonPhraseId[]; type: 'phrase-pairs' }
  | { type: 'word-bank' }
  | { type: 'pairs'; words: LessonWordId[] }

/**
 * A different lesson per level, which is the point: a learner who said they can
 * already hold a conversation should not be shown a picture of a cat.
 *
 * The shape of the screen carries the difference as much as the content does.
 * Beginners get picture cards; from elementary up the pictures disappear and
 * the tiles are text only; intermediate and advanced get the word bank, where
 * nothing is offered to recognise and the sentence has to be built.
 *
 * Option orders are fixed rather than shuffled so tiles do not jump between
 * renders.
 */
const PLANS: Record<LessonLevel, LessonExercise[]> = {
  advanced: [
    {
      options: ['goodMorning', 'goodbye', 'thankYou'],
      prompt: 'goodMorning',
      type: 'phrase-meaning',
    },
    { type: 'word-bank' },
    { phrases: LESSON_PHRASE_IDS, type: 'phrase-pairs' },
    {
      options: ['howAreYou', 'goodbye', 'goodMorning'],
      prompt: 'goodbye',
      type: 'phrase-meaning',
    },
  ],
  beginner: [
    { options: ['dog', 'cat', 'milk'], prompt: 'cat', type: 'image-choice' },
    { options: ['mom', 'dog', 'water'], prompt: 'dog', type: 'image-choice' },
    { type: 'pairs', words: LESSON_WORD_IDS },
    { options: ['cat', 'milk', 'water'], prompt: 'water', type: 'meaning' },
  ],
  elementary: [
    { options: ['dog', 'cat', 'milk'], prompt: 'cat', type: 'image-choice' },
    { type: 'pairs', words: LESSON_WORD_IDS },
    { options: ['cat', 'milk', 'water'], prompt: 'water', type: 'meaning' },
    {
      options: ['thankYou', 'goodMorning', 'goodbye'],
      prompt: 'thankYou',
      type: 'phrase-meaning',
    },
  ],
  intermediate: [
    { phrases: LESSON_PHRASE_IDS, type: 'phrase-pairs' },
    {
      options: ['howAreYou', 'thankYou', 'goodbye'],
      prompt: 'howAreYou',
      type: 'phrase-meaning',
    },
    { type: 'word-bank' },
    { options: ['milk', 'water', 'mom'], prompt: 'milk', type: 'meaning' },
  ],
}

export function getLessonPlan(level: LessonLevel): LessonExercise[] {
  return PLANS[level]
}

/**
 * Three rather than five. The lesson is four exercises long and a missed one is
 * asked again, so at five the learner could not run out however badly it went,
 * and the hearts were decoration.
 */
export const LESSON_STARTING_HEARTS = 3
